import React, { useCallback, useEffect, useMemo, useState, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, ClickAwayListener } from "@mui/material";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleModal } from "@components/TaleModal";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { companyService } from "@/api/companies";
import { lazily } from "react-lazily";
import { useTranslation } from "@/hooks/useTranslation";

const { InternalServerError } = lazily(() =>
    import("@components/Errors/InternalServerError")
);

// ============================================================
// Add/Edit Company User Popup Component
// ============================================================
const AddCompanyUserPopup = ({
                                 onClose,
                                 onValidate,
                                 existingUser = null,
                                 companyId = null,
                                 isEmbedded = false
                             }) => {
    const { t } = useTranslation();
    const isEditMode = !!existingUser;

    const [formData, setFormData] = useState({
        firstname: "",
        lastname: "",
        email: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        newPassword: "", // For edit mode optional password change
        confirmNewPassword: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Populate form when editing
    useEffect(() => {
        if (existingUser) {
            setFormData((prev) => ({
                ...prev,
                firstname: existingUser.firstname || "",
                lastname: existingUser.lastname || "",
                email: existingUser.email || "",
                phoneNumber: existingUser.phoneNumber || "",
            }));
        }
    }, [existingUser]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleValidate = async () => {
        if (disabled) return;
        setError(null);

        // Validation for create mode
        if (!isEditMode) {
            if (formData.password.length < 6) {
                setError(t("manageCompany.form.passwordTooShort"));
                return;
            }
            if (formData.password !== formData.confirmPassword) {
                setError(t("manageCompany.form.passwordMismatch"));
                return;
            }
        }

        // For edit mode, if new password is provided, validate it
        if (isEditMode && formData.newPassword) {
            if (formData.newPassword.length < 6) {
                setError(t("companyUsers.newPasswordTooShort"));
                return;
            }
            if (formData.newPassword !== formData.confirmNewPassword) {
                setError(t("companyUsers.newPasswordMismatch"));
                return;
            }
        }

        try {
            setLoading(true);
            let result;

            if (isEditMode) {
                return;
            } else {
                result = await companyService.addCompanyMember(companyId, {
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    email: formData.email,
                    phoneNumber: formData.phoneNumber || null,
                    password: formData.password,
                });
            }

            onValidate(result);
            onClose();
        } catch (err) {
            console.error("Error saving user:", err.message);
            setError(err?.response?.data?.message || t("manageCompany.form.genericError"));
        } finally {
            setLoading(false);
        }
    };

    // Determine if submit should be disabled
    const disabled = isEditMode
        ? !formData.firstname || !formData.lastname || !formData.email
        : !formData.firstname ||
        !formData.lastname ||
        !formData.email ||
        !formData.password;

    return (
        <ClickAwayListener onClickAway={onClose}>
            <div
                style={
                    isEmbedded
                        ? {}
                        : {
                            padding: 20,
                            borderRadius: 8,
                            boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
                        }
                }
                className="flex flex-col !gap-5 bg-white w-full md:w-[480px]"
            >
                {!isEmbedded && (
                    <div
                        style={{
                            textAlign: "justify",
                            color: "black",
                            fontSize: 24,
                            fontFamily: "revert",
                            fontWeight: "700",
                            wordWrap: "break-word",
                        }}
                    >
                        {isEditMode ? t("companyUsers.editUserTitle") : t("companyUsers.addUserTitle")}
                    </div>
                )}

                <div className={`${isEmbedded ? "" : "overflow-y-scroll !max-h-[700px]"} -z-0`}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", gap: 8 }}>
                            <TaleInput
                                label={t("account.firstNameLabel")}
                                type="text"
                                name="firstname"
                                value={formData.firstname}
                                placeholder={t("account.firstNameLabel")}
                                onChange={handleInputChange}
                            />
                            <TaleInput
                                label={t("account.lastNameLabel")}
                                type="text"
                                name="lastname"
                                value={formData.lastname}
                                placeholder={t("account.lastNameLabel")}
                                onChange={handleInputChange}
                            />
                        </div>

                        <TaleInput
                            label={t("manageCompany.form.emailLabel")}
                            type="email"
                            name="email"
                            value={formData.email}
                            placeholder="user@company.com"
                            onChange={handleInputChange}
                        />

                        <TaleInput
                            label={t("manageCompany.form.phoneNumberOptionalLabel")}
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                        />

                        {!isEditMode ? (
                            <>
                                <TaleInput
                                    label={t("manageCompany.form.passwordLabel")}
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    placeholder={t("manageCompany.form.passwordPlaceholder")}
                                    onChange={handleInputChange}
                                />
                                <TaleInput
                                    label={t("manageCompany.form.confirmPasswordLabel")}
                                    type="password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    placeholder={t("manageCompany.form.confirmPasswordPlaceholder")}
                                    onChange={handleInputChange}
                                />
                            </>
                        ) : (
                            <>
                                <div
                                    style={{
                                        color: "#666",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        marginTop: 8,
                                    }}
                                >
                                    {t("companyUsers.changePasswordOptional")}
                                </div>
                                <TaleInput
                                    label={t("companyUsers.newPasswordLabel")}
                                    type="password"
                                    name="newPassword"
                                    value={formData.newPassword}
                                    placeholder={t("companyUsers.leaveEmptyToKeep")}
                                    onChange={handleInputChange}
                                />
                                <TaleInput
                                    label={t("companyUsers.confirmNewPasswordLabel")}
                                    type="password"
                                    name="confirmNewPassword"
                                    value={formData.confirmNewPassword}
                                    placeholder={t("companyUsers.repeatNewPassword")}
                                    onChange={handleInputChange}
                                />
                            </>
                        )}

                        {error && (
                            <span style={{ color: "#dc2626", fontSize: 13, textAlign: "center" }}>
                {error}
              </span>
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        className="!font-semibold !border !border-[#111] !text-[#111] basis-1/3 !rounded-2xl !normal-case"
                    >
                        {t("common.cancel")}
                    </Button>
                    <TaleButton
                        role="business"
                        onClick={handleValidate}
                        disabled={disabled}
                        className={`!rounded-2xl !font-semibold basis-1/3 ${disabled ? "!text-[#666]" : ""}`}
                        loading={loading}
                        loadingCircleSize={25}
                    >
                        {isEditMode ? t("common.save") : t("manageCompany.form.submitButton")}
                    </TaleButton>
                </div>
            </div>
        </ClickAwayListener>
    );
};

// ============================================================
// Modal Component for Delete Confirmation
// ============================================================

// ============================================================
// User Row Component
// ============================================================
const UserRow = ({ user, onEdit, onDelete }) => {
    const initials = `${user.firstname?.[0] || ""}${user.lastname?.[0] || ""}`.toUpperCase();

    return (
        <tr className="border-b hover:bg-gray-50 transition-colors">
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-xs font-bold">
                        {initials || "U"}
                    </div>
                    <span className="font-medium text-gray-900">
            {user.firstname} {user.lastname}
          </span>
                </div>
            </td>
            <td className="px-4 py-3 text-gray-600">{user.email}</td>
            <td className="px-4 py-3 text-gray-600">{user.phoneNumber || <span className="text-gray-300">—</span>}</td>
            {/*<td className="px-4 py-3 text-sm text-gray-500">*/}
            {/*    {user.createdAt*/}
            {/*        ? new Date(user.createdAt).toLocaleDateString("fr-FR")*/}
            {/*        : <span className="text-gray-300">—</span>}*/}
            {/*</td>*/}
        </tr>
    );
};

// ============================================================
// Main Company Users Component
// ============================================================
const CompanyUsers = ({ initialCompanyId = null }) => {
    const { t } = useTranslation();
    const PAGE_SIZE = 8;

    const [currentPage, setCurrentPage] = useState(0);
    const [searchFilter, setSearchFilter] = useState("");
    const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId);
    const [companies, setCompanies] = useState([]);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [members, setMembers] = useState([]);

    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [isCompanyLoading, setIsCompanyLoading] = useState(true);

    // Fetch companies list for dropdown (if no initialCompanyId)
    const { data: companiesData, isLoading: companiesLoading } = useQuery({
        queryKey: ["companies-list"],
        queryFn: () => companyService.getCompanies(0, 100),
        enabled: !initialCompanyId,
        retry: false,
    });

    useEffect(() => {
        if (companiesData) {
            const companyList = Array.isArray(companiesData) ? companiesData : companiesData.result || [];
            setCompanies(companyList);
            if (!initialCompanyId && companyList.length > 0 && !selectedCompanyId) {
                setSelectedCompanyId(companyList[0].id);
            }
        }
        setIsCompanyLoading(false);
    }, [companiesData, initialCompanyId]);

    // Fetch members for selected company
    const {
        isPending: loading,
        data: membersData,
        error,
        refetch
    } = useQuery({
        queryKey: ["company-members", selectedCompanyId, currentPage, searchFilter],
        queryFn: () => companyService.getCompanyMembersPaginated(
            selectedCompanyId,
            currentPage,
            PAGE_SIZE,
            searchFilter
        ),
        enabled: !!selectedCompanyId,
        retry: false,
    });

    useEffect(() => {
        if (loading || error || !membersData) return;
        setMembers(membersData.result ?? []);
        setTotalPages(membersData.totalPages ?? 1);
        setTotalItems(membersData.totalItems ?? 0);
    }, [membersData, loading, error]);

    const handleSearch = useCallback((value) => {
        setSearchFilter(value);
        setCurrentPage(0);
    }, []);

    const handleCompanyChange = (e) => {
        setSelectedCompanyId(e.target.value);
        setCurrentPage(0);
        setSearchFilter("");
    };

    const filteredMembers = useMemo(() => {
        if (!searchFilter) return members;
        const q = searchFilter.toLowerCase();
        return members.filter(
            (u) =>
                u.firstname?.toLowerCase().includes(q) ||
                u.lastname?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q)
        );
    }, [members, searchFilter]);

    // Create success
    const handleCreateSuccess = useCallback(() => {
        setShowCreateModal(false);
        refetch();
    }, [refetch]);

    const loadingItem = useMemo(
        () => (
            <div className="size-full z-20 absolute flex items-center justify-center grow">
                <TaleLoadingFallback />
            </div>
        ),
        []
    );

    // Get selected company name for display
    const selectedCompanyName = useMemo(() => {
        if (initialCompanyId) return "";
        const company = companies.find(c => c.id === selectedCompanyId);
        return company?.companyName || "";
    }, [companies, selectedCompanyId, initialCompanyId]);

    return (
        <>

            {/* Create modal */}
            {showCreateModal && selectedCompanyId && (
                <TaleModal className="flex items-end justify-end p-4" onClose={() => setShowCreateModal(false)}>
                    <AddCompanyUserPopup
                        companyId={selectedCompanyId}
                        onClose={() => setShowCreateModal(false)}
                        onValidate={handleCreateSuccess}
                    />
                </TaleModal>
            )}

            <TaleDashbaordMainCard className="flex flex-col gap-6">
                {/* Header */}
                <div className="w-full flex justify-between items-center flex-wrap gap-4">
                    <div className="flex flex-col">
                        <h2 className="!text-2xl font-bold !m-0">{t("companyUsers.title")}</h2>
                        <p className="text-gray-600">
                            {selectedCompanyName
                                ? t("companyUsers.subtitleWithCompany", { company: selectedCompanyName })
                                : t("companyUsers.subtitleGeneric")}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <TaleButton
                            role="normal"
                            className="!px-4"
                            onClick={() => setShowCreateModal(true)}
                            disabled={!selectedCompanyId}
                        >
                            + {t("companyUsers.newUserButton")}
                        </TaleButton>
                        <TaleButton role="normal" variant="outlined" onClick={() => refetch()}>
                            <i className="fas fa-sync-alt mr-2 rtl:mr-0 rtl:ml-2"></i>{t("manageCompany.refresh")}
                        </TaleButton>
                    </div>
                </div>

                {/* Company selector (if no initial company id) */}
                {!initialCompanyId && (
                    <div className="flex items-center gap-4">
                        <div className="relative w-full md:w-72">
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("manageCompany.columnCompany")}</label>
                            <select
                                className="px-4 py-2 border rounded-lg w-full bg-white"
                                value={selectedCompanyId || ""}
                                onChange={handleCompanyChange}
                                disabled={isCompanyLoading || companiesLoading}
                            >
                                <option value="" disabled>{t("companyUsers.selectCompanyPlaceholder")}</option>
                                {companies.map((company) => (
                                    <option key={company.id} value={company.id}>
                                        {company.companyName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Search bar */}
                <div className="flex items-center gap-4">
                    <div className="relative w-full md:w-72">
                        <input
                            type="text"
                            placeholder={t("companyUsers.searchPlaceholder")}
                            className="px-4 py-2 border rounded-lg w-full"
                            value={searchFilter}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        {searchFilter && (
                            <button
                                className="absolute right-2 rtl:right-auto rtl:left-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                onClick={() => handleSearch("")}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className={`flex flex-col relative grow size-full ${!filteredMembers.length && !loading ? "items-center justify-center gap-4" : "gap-4"}`}>
                    {loading && loadingItem}
                    {error && !loading && (
                        <div className="size-full z-20 absolute flex items-center justify-center grow">
                            <Suspense fallback={null}>
                                <InternalServerError />
                            </Suspense>
                        </div>
                    )}
                    {!loading && !error && (
                        filteredMembers.length === 0 ? (
                            <>
                                <img alt="Empty box" width={80} height={80} src="/assets/empty-box.svg" />
                                <span className="text-[#333333] text-lg">
                  {selectedCompanyId
                      ? t("companyUsers.noUsersFound")
                      : t("companyUsers.selectCompanyHint")}
                </span>
                                {searchFilter && (
                                    <button onClick={() => handleSearch("")} className="text-blue-600 hover:text-blue-800 text-sm">
                                        {t("manageCompany.clearSearch")}
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="overflow-x-auto rounded-xl border border-gray-100">
                                <table className="w-full text-sm">
                                    <thead>
                                    <tr className="bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide">
                                        <th className="px-4 py-3 font-semibold">{t("companyUsers.columnFullName")}</th>
                                        <th className="px-4 py-3 font-semibold">{t("manageCompany.form.emailLabel")}</th>
                                        <th className="px-4 py-3 font-semibold">{t("companyUsers.columnPhone")}</th>
                                        {/*<th className="px-4 py-3 font-semibold">Date création</th>*/}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredMembers.map((user) => (
                                        <UserRow
                                            key={user.id}
                                            user={user}
                                        />
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}
                </div>

                {/* Paginator */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between pr-16 rtl:pr-0 rtl:pl-16 gap-8 mt-4">
            <span className="text-sm text-gray-500">
              {t("surveys.list.paginationShowing", { from: currentPage * PAGE_SIZE + 1, to: Math.min((currentPage + 1) * PAGE_SIZE, totalItems), total: totalItems })}
            </span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setCurrentPage(p => p - 1)}
                                disabled={currentPage === 0}
                                className="w-9 h-9 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <svg className="rtl:-scale-x-100" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.83333 0.5L0.5 5.83333M0.5 5.83333L5.83333 11.1667M0.5 5.83333H11.1667" stroke="#333333" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i)
                                .filter(i => i === 0 || i === totalPages - 1 || Math.abs(i - currentPage) <= 1)
                                .reduce((acc, i, idx, arr) => {
                                    if (idx > 0 && i - arr[idx - 1] > 1) acc.push("...");
                                    acc.push(i);
                                    return acc;
                                }, [])
                                .map((item, idx) =>
                                    item === "..." ? (
                                        <span key={`ellipsis-${idx}`} className="w-9 h-9 flex items-center justify-center text-gray-400">…</span>
                                    ) : (
                                        <button
                                            key={item}
                                            onClick={() => setCurrentPage(item)}
                                            className={`w-9 h-9 rounded-2xl flex items-center justify-center text-sm font-medium transition-all ${
                                                item === currentPage
                                                    ? "bg-[#333333] text-white shadow-sm"
                                                    : "border-2 border-gray-800 text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            {item + 1}
                                        </button>
                                    )
                                )}
                            <button
                                onClick={() => setCurrentPage(p => p + 1)}
                                disabled={currentPage === totalPages - 1}
                                className="w-9 h-9 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <svg className="rtl:-scale-x-100" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.83333 11.1667L11.1667 5.83333M11.1667 5.83333L5.83333 0.5M11.1667 5.83333H0.5" stroke="#333333" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </TaleDashbaordMainCard>
        </>
    );
};

export default CompanyUsers;